import {StudentsByApp} from "@/app/types/Student";
import UserInfo from "@/app/types/UserInfo";
import {ConsultationRequest} from "@/lib/legacyApi/addConsultation";
import {Key} from "react-aria-components";

export type ContactFormParentFields = {
	parentFirstName: string;
	parentLastName: string;
	parentEmail: string;
	parentPhoneNumber: string;
};

/**
 * This is an object field for now.  If we wanted to select arbitrary campuses
 * we may want to make this an array, but it would have implications for the UI
 * side of things to do it that way, so I'm leaving it as an object for now.
 */
export type ContactFormSelectedCampuses = {
	primaryCampus: Key;
	secondaryCampus?: Key;
};

export type ContactFormSelectedCampusHandlers = {
	setPrimaryCampus: (newPrimaryCampus: Key) => void;
	setSecondaryCampus: (newSecondaryCampus: Key) => void;
	resetCampusSelection: () => void;
};

export type ContactFormPlatformStudent = {
	studentPlatformId: string;
	studentName: string;
};

export type ContactFormSelectedStudents = {
	initialStudentName: string;
	newStudentNames: Record<number, string>;
	selectedAcademyStudents: string[];
	selectedPlatformStudents: string[];
	platformStudentNames: Record<string, string>;
};

export type ContactFormStudentHandlers = {
	onInitialStudentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	setNewStudent: (key: number, value: string) => void;
	setNewStudents: (newStudents: Record<number, string>) => void;
	setSelectedAcademyStudents: (selection: string[]) => void;
	setSelectedPlatformStudents: (selection: string[]) => void;
	onPlatformStudentNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	resetStudentFields: () => void;
};

export type ContactFormState = {
	selectedCampuses: ContactFormSelectedCampuses;
	parentFields: ContactFormParentFields;
	studentFields: ContactFormSelectedStudents;
	isOptedIntoEmails: boolean;
};

export const composeConsultationRequest: (
	formState: ContactFormState,
	userInfo?: UserInfo | null,
	clientDeviceId?: string,
	userStudents?: StudentsByApp
) => ConsultationRequest = (
	{parentFields, studentFields, selectedCampuses, isOptedIntoEmails},
	userInfo = null,
	clientDeviceId,
	userStudents
) => {
	// concat the initial student field with the additional new student fields (if they exist)
	const allNewStudents = [
		studentFields.initialStudentName ?? "",
		...Object.values(studentFields.newStudentNames ?? {}),
	].filter((studentName) => studentName);
	const existingStudents = userStudents?.academyStudents.filter((a) =>
		studentFields.selectedAcademyStudents.includes(a.userId)
	);
	return {
		parentFirstName: userInfo?.first_name || parentFields.parentFirstName,
		parentLastName: userInfo?.last_name || parentFields.parentLastName,
		parentEmail: userInfo?.email || parentFields.parentEmail,
		parentPhoneNumber: parentFields.parentPhoneNumber,
		campusId: selectedCampuses.primaryCampus.toString(),
		additionalCampusId: selectedCampuses.secondaryCampus?.toString(),
		newStudentNames: allNewStudents,
		existingStudents,
		uninitializedStudents: studentFields.selectedPlatformStudents?.map(
			(studentId) => {
				return {
					platformId: studentId,
					firstName: studentFields.platformStudentNames[studentId],
				};
			}
		),
		// TODO: make this a boolean param instead
		optin: isOptedIntoEmails ? "true" : "",
		cdid: clientDeviceId,
	};
};

export const hasSelectedChildren = (request: ConsultationRequest) => {
	// check for children
	if (
		!request.existingStudents?.length &&
		!request.uninitializedStudents?.length &&
		!request.newStudentNames?.length
	) {
		return false;
	}

	return true;
};
